'use client';

import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
} from '@workspace/ui/components/sidebar';
import { Icon } from '../ui';
import { cn } from '@workspace/ui/lib/utils';
import { useProfile } from '@/src/hooks/use-profile';
import { HEADER_HEIGHT } from '@/src/constants/constants';
import { UserProgressBlock } from '@/src/sections/social/components/UserProgressBlock';
import { usePathname, useSearchParams } from 'next/navigation';
import { useResponsive } from '@/src/hooks/use-responsive';
import { useCycleResetTime } from '@/src/hooks/use-cycle-reset-time';
import { useSidebarStore } from '@/src/sections/social/store/sidebar-store';
import { ButtonsBlockVertical } from '@/src/sections/home/components/ButtonsBlock';
import Link from 'next/link';
import Image from 'next/image';
import { Avatar, AvatarImage } from '@workspace/ui/components/avatar';
import { Suspense } from 'react';
import { TooltipComponent } from './TooltipComponent';

const anonymousItems = [
  {
    title: 'Home',
    url: '/',
    icon: 'home',
  },
  {
    title: 'Stories',
    url: '/explore-stories',
    icon: 'stories',
  },
  {
    title: 'Projects',
    url: '/browse-projects',
    icon: 'project',
  },
  // {
  //   title: 'Campaigns',
  //   url: '/',
  //   icon: 'campaigns',
  // },
  // {
  //   title: 'Connect',
  //   url: '/',
  //   icon: 'connect',
  // },
];

const items = [
  {
    title: 'Home',
    url: '/',
    icon: 'home',
  },
  {
    title: 'Stories',
    url: '/explore-stories',
    icon: 'stories',
  },
  {
    title: 'Projects',
    url: '/browse-projects',
    icon: 'project',
  },
  {
    title: 'Library',
    url: '/library',
    icon: 'library',
  },
  // {
  //   title: 'Campaigns',
  //   url: '/',
  //   icon: 'campaigns',
  // },
  // {
  //   title: 'Connect',
  //   url: '/',
  //   icon: 'connect',
  // },
  {
    title: 'Followers',
    url: '/network/?tab=followers',
    icon: 'followers',
  },
  {
    title: 'Earnings',
    url: '/finances',
    icon: 'earnings',
  },
];

export const MainSidebar = () => {
  const { data: profile, isLoading } = useProfile();
  const pathname = usePathname();
  const { targetIso, economyData } = useCycleResetTime();
  const { opened, toggleOpened, setOpened } = useSidebarStore();
  const is2xlUp = useResponsive('up', '2xl');
  const isAnonymous = profile?.type === 'anonymous';
  const isOverlayPage = pathname !== '/';
  const shouldShowOverlay = opened && isOverlayPage && !is2xlUp;
  const isMobile = useResponsive('down', 'md');

  if (isLoading) return null;

  return (
    <>
      {shouldShowOverlay && (
        <div
          className="fixed inset-0 z-30 bg-black/20 backdrop-blur-[2px]"
          onClick={toggleOpened}
        />
      )}

      <SidebarProvider
        open={opened}
        onOpenChange={setOpened}
        defaultOpen={false}
      >
        <Sidebar
          collapsible="icon"
          className={cn(
            'border-r border-gray-200 bg-white',
            'transition-[width] duration-300 ease-in-out',
            `fixed left-0 top-[68px] h-[calc(100vh-${HEADER_HEIGHT}px)] z-40 flex flex-col`,
            opened ? `w-[310px]!` : `w-20!`,
          )}
        >
          <SidebarHeader
            className={cn(
              'p-2 md:px-5 mx-2 md:mx-0 flex items-center justify-center mb-3  border-b pb-5 md:pb-0 md:border-b-0 border-gray-300 ',
              {
                'justify-start items-start': opened,
                'flex-col': isAnonymous,
              },
            )}
          >
            {isAnonymous ? (
              <>
                <SidebarTrigger
                  className={cn('md:flex hidden items-center justify-center', {
                    '': opened,
                  })}
                >
                  <Icon
                    name="collapse"
                    className={cn('size-6 transition-transform duration-300')}
                  />
                </SidebarTrigger>
                <Link
                  href="/"
                  className={cn('hidden', {
                    inline: isMobile,
                  })}
                >
                  <Image
                    src="/icons/logoHero.png"
                    alt="logo"
                    width={40}
                    height={40}
                    priority
                    className="object-contain"
                  />
                </Link>
                <h2 className="font-grotesk font-bold text-base! md:hidden block my-3">
                  Join the world of{' '}
                  <span className="text-pink-accent">gaming</span>.
                </h2>
                {isMobile && <ButtonsBlockVertical isAnonymous={true} />}
              </>
            ) : (
              <div className="flex md:flex-row flex-row-reverse justify-between w-full items-start">
                <SidebarTrigger
                  className={cn('flex items-center justify-center', {
                    'rotate-180': opened,
                  })}
                >
                  <Icon
                    name="collapse"
                    className={cn('size-6 transition-transform duration-300')}
                  />
                </SidebarTrigger>
                <Link
                  href={profile?.uniqueLink as string}
                  className="flex md:hidden flex-col hover:cursor-pointer"
                >
                  <Avatar
                    className={cn('size-8 flex justify-center items-center')}
                  >
                    <AvatarImage
                      src={profile?.avatarUrl || '/images/default-avatar.png'}
                      alt="Profile"
                    />
                  </Avatar>
                  <span className="mt-1 font-semibold text-sm">
                    {profile?.fullName || 'User'}
                  </span>
                  <span className="mt-1 font-light text-sm">
                    {`@${profile?.uniqueLink}`}
                  </span>
                  <div className="flex items-center gap-1 h-2 mt-2 ml-1">
                    <Icon name="lvl" className="w-5 h-2" />
                    <Icon name={`${profile?.level ?? 0}`} className="h-2 w-2" />
                  </div>
                </Link>
              </div>
            )}
          </SidebarHeader>
          <SidebarContent className="md:px-7 px-3 flex flex-col justify-between flex-1">
            <SidebarGroup className="px-0!">
              <SidebarGroupContent>
                <SidebarGroupContent>
                  <Suspense fallback={null}>
                    <SidebarMenuContent
                      opened={opened}
                      items={isAnonymous ? anonymousItems : items}
                      setOpened={setOpened}
                    />
                  </Suspense>
                </SidebarGroupContent>
              </SidebarGroupContent>
            </SidebarGroup>

            <UserProgressBlock
              isOpen={opened}
              profile={profile}
              targetIso={targetIso}
              economyData={economyData}
            />
          </SidebarContent>
        </Sidebar>
      </SidebarProvider>
    </>
  );
};

const SidebarMenuContent = ({ opened, items, setOpened }: any) => {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const isItemActive = (itemUrl: string) => {
    const base = itemUrl.split('?')[0] || itemUrl;
    const normalizedBase =
      base.endsWith('/') && base !== '/' ? base.slice(0, -1) : base;

    const normalizedPathname =
      pathname.endsWith('/') && pathname !== '/'
        ? pathname.slice(0, -1)
        : pathname;

    if (normalizedPathname !== normalizedBase) return false;

    const queryString = itemUrl.includes('?') ? itemUrl.split('?')[1] : '';
    if (!queryString) return true;

    const itemParams = new URLSearchParams(queryString);
    for (const [k, v] of itemParams.entries()) {
      if (searchParams.get(k) !== v) return false;
    }
    return true;
  };

  return (
    <SidebarMenu className="md:space-y-6 space-y-2 flex flex-col gap-6">
      {items.map((item: any) => {
        const active = isItemActive(item.url);
        return (
          <TooltipComponent
            text={item.title}
            className={cn('max-w-[100px] max-h-15 h-fit text-black! md:mb-0', {
              hidden: opened,
            })}
            side="right"
          >
            <SidebarMenuItem
              key={item.title}
              className={cn(
                'list-none! mb-0! flex items-center justify-center w-fit',
                {
                  'justify-start items-start px-1': opened,
                },
              )}
            >
              <SidebarMenuButton asChild>
                <Link
                  href={item.url}
                  onClick={() => setOpened(false)}
                  className={cn(
                    'flex items-center justify-center hover:bg-transparent transition-colors p-0! h-fit mb-0.5',
                    {
                      'justify-start items-start': opened,
                    },
                    active &&
                      'after:content-[""] after:absolute after:left-0 after:right-0 after:-bottom-0 after:h-[2px] after:bg-pink-accent',
                  )}
                >
                  <Icon
                    name={item.icon}
                    className="size-6! text-black flex-shrink-0 p-0"
                  />
                  {opened && (
                    <span
                      className={cn(
                        'text-base font-semibold whitespace-nowrap',
                        'transition-all duration-200 origin-left',
                        opened
                          ? 'opacity-100 scale-100'
                          : 'opacity-0 scale-95 w-0 overflow-hidden',
                      )}
                    >
                      {item.title}
                    </span>
                  )}
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </TooltipComponent>
        );
      })}
    </SidebarMenu>
  );
};
